import React, { FormEvent } from "react";
import IntegrationFlowBase, { IProps as IIntegrationFlowBaseProps } from "./integration-flow-base";
import IntegrationProviders from "../integration-providers";
import { Transition } from "semantic-ui-react";
import { Button, Carousel, Col, Container, Form, Image } from "react-bootstrap";
import { STRINGS } from "../../../../../../../localization";
import { IIntegrationsStore } from "../../../../../../mobx-stores/old-integrations-store";
import { observer } from "mobx-react";
import { DashboardIntegrationType } from "@acumen/dashboard-common";

export interface IProps extends IIntegrationFlowBaseProps {
	oldIntegrationsStore: IIntegrationsStore;
}

enum SlackIntegrationFlowSteps {
	InstallStep,
	EnterInstallDataStep,
	PendingLinkingStep,
	SuccessLinkingStep
}

@observer
export default class SlackIntegrationFlow extends IntegrationFlowBase<IProps> {
	protected initialState = {
		internalStep: SlackIntegrationFlowSteps.InstallStep,
		formValidated: false,
		inputInstallCode: undefined,
		inputInstallState: undefined,
		shakeButton: false
	};

	public state = {...this.initialState};

	protected async initFlow() {
		const { oldIntegrationsStore, onInit } = this.props;
		const { integrationWizard } = oldIntegrationsStore;

		await oldIntegrationsStore.createSlackInstallLink();

		const { installLink } = integrationWizard;

		if (installLink) {
			onInit(STRINGS.INTEGRATION_SLACK_FLOW_TITLE);
		}
	}

	private onInstallClick = (): void => {
		const { oldIntegrationsStore } = this.props;
		const { integrationWizard } = oldIntegrationsStore;
		const { installLink } = integrationWizard;

		window.open(installLink);

		this.setState({internalStep: SlackIntegrationFlowSteps.EnterInstallDataStep});
	}

	private onRedirectedUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		try {
			const url: string = e.target.value;
			const inputInstallCode: string = url.split("code=")[1].split("&")[0];
			const inputInstallState: string = url.split("state=")[1].split("&")[0];

			this.setState({inputInstallCode, inputInstallState});
		} catch (ex) {
			this.setState({inputInstallCode: undefined, inputInstallState: undefined});
		}
	}

	private onInstallDataSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const { inputInstallCode, inputInstallState } = this.state;
		const { oldIntegrationsStore } = this.props;
		const { integrationWizard } = oldIntegrationsStore;
		const { integrationId } = integrationWizard;

		if (!inputInstallCode || !inputInstallState) {
			e.stopPropagation();
			this.setState({formValidated: true, shakeButton: true});
			return;
		}

		this.setState({internalStep: SlackIntegrationFlowSteps.PendingLinkingStep});

		await oldIntegrationsStore.updateSlackInstallData({
			integrationId,
			slackInstallCode: inputInstallCode,
			slackInstallState: inputInstallState
		});

		if (integrationWizard.slackInstallCode) {
			this.setState({internalStep: SlackIntegrationFlowSteps.SuccessLinkingStep});
		}
	}

	private renderInstallStep() {
		return (
			<>
				<p>
					{STRINGS.INTEGRATION_SLACK_FLOW_INSTALL_STEP_TEXT}
				</p>
				<br/>
				<Button className="btn-rounded install-button" size="lg" onClick={this.onInstallClick}>
					{STRINGS.INTEGRATION_SLACK_FLOW_INSTALL_STEP_BUTTON}
				</Button>
			</>
		);
	}

	private renderEnterInstallDataStep() {
		const { formValidated, shakeButton } = this.state;

		return (
			<>
				<p>
					{STRINGS.INTEGRATION_SLACK_FLOW_ENTER_DATA_STEP_TEXT}
				</p>
				<br/>
				<Form
					noValidate={true}
					validated={formValidated}
					onSubmit={this.onInstallDataSubmit}
				>
					<Form.Row>
						<Form.Group as={Col} md="4">
							<Form.Label>{STRINGS.INTEGRATION_SLACK_FLOW_ENTER_DATA_STEP_REDIRECTED_URL}</Form.Label>
							<Form.Control
								className="integration-flow-step-text-input"
								type="url"
								placeholder={STRINGS.INTEGRATION_SLACK_FLOW_ENTER_DATA_STEP_PASTE_URL}
								required={true}
								onChange={this.onRedirectedUrlChange}
								autoComplete="off"
							/>
							<Form.Control.Feedback type="invalid">
								{STRINGS.INTEGRATION_SLACK_FLOW_ENTER_DATA_URL_STEP_VALIDATION}
							</Form.Control.Feedback>
						</Form.Group>
					</Form.Row>
					<br/>
					<Form.Row>
						<Transition
							visible={shakeButton}
							animation="shake"
							duration={800}
							onComplete={() => this.setState({shakeButton: false})}
							mountOnShow={false}
						>
							<Button className="btn-rounded install-button" size="lg" type="submit">
								{STRINGS.INTEGRATION_SLACK_FLOW_ENTER_DATA_URL_STEP_SUBMIT}
							</Button>
						</Transition>
					</Form.Row>
				</Form>
			</>
		);
	}

	public render() {
		const { internalStep } = this.state;
		const provider = IntegrationProviders.find(p => p.integrationType === DashboardIntegrationType.Slack);

		if (!provider) { return null; }

		return (
			<Container className="integration-flow-step-root">
				<div className="integration-flow-step-logo-header">
					<Image src={provider.logo} alt={provider.title} />
					<hr/>
				</div>
				<div>
					<Carousel activeIndex={internalStep} controls={false} indicators={false} onSelect={(() => false)}>
						<Carousel.Item>
							{this.renderInstallStep()}
						</Carousel.Item>
						<Carousel.Item>
							{this.renderEnterInstallDataStep()}
						</Carousel.Item>
						<Carousel.Item>
							{this.renderPendingStep(STRINGS.INTEGRATION_SLACK_FLOW_LINKING_STEP_TEXT)}
						</Carousel.Item>
						<Carousel.Item>
							{this.renderSuccessStep(STRINGS.INTEGRATION_SLACK_FLOW_SUCCESS_STEP_TEXT)}
						</Carousel.Item>
					</Carousel>
				</div>
				<br/>
			</Container>
		);
	}
}
